import React from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  IconName,
  SwitchButton,
} from '@lightricks/react-design-system';
import {AvatarSize} from '@contentlab/stores/contentLabStore';
import translate from '@/utils/translate';
import Divider from '@/components/divider';

const SizeOptionsItemContainer = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== 'isSelected',
})<{isSelected: boolean}>(({isSelected}) => ({
  'height': '40px',
  'width': '100%',
  'padding': '8px',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'boxSizing': 'border-box',
  'borderRadius': '8px',
  'flex': '1',
  'cursor': 'pointer',
  'border': `1.5px solid ${
    isSelected
      ? designSystemToken('semantic.bg.neutral')
      : designSystemToken('semantic.bg.neutral-inverse')
  }`,
  'backgroundColor': isSelected
    ? 'transparent'
    : designSystemToken('semantic.bg.primary'),
  'color': designSystemToken('semantic.fg.primary'),
  '&:hover': {
    borderColor: designSystemToken('semantic.bg.neutral'),
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
}));

const SizeOptions: Record<AvatarSize, {icon: IconName}> = {
  [AvatarSize.SMALL]: {
    icon: 'CharacterSize-Small',
  },
  [AvatarSize.MEDIUM]: {
    icon: 'CharacterSize-Medium',
  },
  [AvatarSize.LARGE]: {
    icon: 'CharacterSize-Large',
  },
};

interface SizeOptionItemProps {
  size: AvatarSize;
  isSelected: boolean;
  onClick: () => void;
}

function SizeOptionItem({size, isSelected, onClick}: SizeOptionItemProps) {
  const data = SizeOptions[size];

  return (
    <SizeOptionsItemContainer isSelected={isSelected} onClick={onClick}>
      <Icon name={data.icon} size="medium" appearance="neutral" />
    </SizeOptionsItemContainer>
  );
}

interface AvatarSiseSectionProps {
  testID?: string;
  avatarSize: AvatarSize;
  cropAvatar: boolean;
  onSizeChange: (size: AvatarSize) => void;
  toggleCropAvatar: () => void;
}

function AvatarSizeSection({
  testID,
  avatarSize,
  cropAvatar,
  onSizeChange,
  toggleCropAvatar,
}: AvatarSiseSectionProps) {
  return (
    <Stack direction="column" padding="0px 0px 16px 0px">
      <Stack
        data-testid={testID}
        direction="row"
        gap="16px"
        width="100%"
        justifyContent="space-around"
        boxSizing="border-box"
      >
        {Object.keys(SizeOptions).map((sizeKey) => {
          const size = sizeKey as AvatarSize;
          return (
            <SizeOptionItem
              key={size}
              size={size}
              isSelected={avatarSize === size}
              onClick={() => onSizeChange(size)}
            />
          );
        })}
      </Stack>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Body size="md" color={designSystemToken('semantic.fg.primary')}>
          {translate('Crop to head & shoulders')}
        </Body>
        <SwitchButton
          checked={cropAvatar}
          onChange={toggleCropAvatar}
          checkedTrackColor={designSystemToken('semantic.bg.neutral')}
        />
      </Box>
    </Stack>
  );
}

export default AvatarSizeSection;
